// ./components/ExamGrading/components/ReOrderDialog.js
import * as React from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Stack,
	LinearProgress,
	FormControlLabel,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
} from "@mui/material";
import { useAtomValue } from "jotai";
import { examProcessingStateAtom } from "../../../components/Atoms/atoms";
import { processExam } from "../../../components/Api/ExamGradingEndpoints";

import { read, writeFileXLSX, utils } from "@e965/xlsx";

export default function ReOrderDialog({
	open,
	onClose,
	token,
	reqnum,
	onNormalized,
	answerKeyArray,
	embedded = false,
}) {
	const examProcessingState = useAtomValue(examProcessingStateAtom);
	const isVisible = embedded || open;

	const [busy, setBusy] = React.useState(false);
	const [error, setError] = React.useState("");
	const [strictVersionMatch, setStrictVersionMatch] = React.useState(true);
	const [file, setFile] = React.useState(null);
	const [versions, setVersions] = React.useState([]);
	const [baseItemNos, setBaseItemNos] = React.useState([]);
	const [rows, setRows] = React.useState([]);
	const [baseVersion, setBaseVersion] = React.useState("");
	const [baseColumnIndex, setBaseColumnIndex] = React.useState(0);

	const getVersionLabel = React.useCallback(
		(key, index) => (key?.version ?? "").toString().trim() || String(index + 1),
		[],
	);

	const resetAll = React.useCallback(() => {
		setBusy(false);
		setError("");
		setFile(null);
		setStrictVersionMatch(true);
		setVersions([]);
		setBaseItemNos([]);
		setRows([]);
		setBaseVersion("");
		setBaseColumnIndex(0);
	}, []);

	const deriveBaseItemNos = React.useCallback((baseKey, header) => {
		if (baseKey?.items && Array.isArray(baseKey.items) && baseKey.items.length > 0) {
			const sorted = [...baseKey.items].sort((a, b) => Number(a.item_no) - Number(b.item_no));
			const filtered = sorted.filter((it) => (it?.omit ?? "N") !== "Y");
			if (filtered.length > 0) return filtered.map((it) => Number(it.item_no));
		}

		const lq = Number(header?.lqnum ?? 1);
		const hq = Number(header?.hqnum ?? lq);
		const values = [];
		for (let i = lq; i <= hq; i++) values.push(i);
		return values;
	}, []);

	const tryInitFromKeys = React.useCallback(() => {
		setError("");
		if (!Array.isArray(answerKeyArray) || answerKeyArray.length === 0) {
			setVersions([]);
			setBaseItemNos([]);
			setRows([]);
			setBaseVersion("");
			setBaseColumnIndex(0);
			return;
		}

		const headerVersions = answerKeyArray.map((key, index) => getVersionLabel(key, index));
		const baseRowCount = deriveBaseItemNos(answerKeyArray[0], examProcessingState?.header).length;
		const columnCount = headerVersions.length;
		const initialRows = Array.from({ length: baseRowCount }, (_, rowIndex) => {
			const row = new Array(columnCount).fill("");
			row[0] = String(rowIndex + 1);
			return row;
		});
		const identityRows = Array.from({ length: baseRowCount }, (_, index) => index + 1);

		setVersions(headerVersions);
		setBaseItemNos(identityRows);
		setRows(initialRows);
		setBaseVersion(headerVersions[0] || "");
		setBaseColumnIndex(0);
	}, [answerKeyArray, deriveBaseItemNos, examProcessingState?.header, getVersionLabel]);

	React.useEffect(() => {
		if (!isVisible) return;
		tryInitFromKeys();
	}, [isVisible, tryInitFromKeys]);

	const handleClose = React.useCallback(() => {
		if (busy) return;
		resetAll();
		if (!embedded) onClose?.();
	}, [busy, embedded, onClose, resetAll]);

	const getKeyByVersion = React.useCallback(
		(versionLabel) =>
			(answerKeyArray || []).find(
				(key, index) => getVersionLabel(key, index) === String(versionLabel ?? "").trim(),
			),
		[answerKeyArray, getVersionLabel],
	);

	const getRowCountForVersion = React.useCallback(
		(versionLabel) => {
			const key = getKeyByVersion(versionLabel);
			return deriveBaseItemNos(key, examProcessingState?.header).length;
		},
		[deriveBaseItemNos, examProcessingState?.header, getKeyByVersion],
	);

	const sniffDelimiter = (text) => (/\t/.test(text) ? "\t" : ",");

	const parseCSVText = (text) => {
		const delimiter = sniffDelimiter(text);
		const lines = text.trim().split(/\r?\n/);
		return lines.map((line) => line.split(delimiter).map((value) => value.trim()));
	};

	const parseXLSXFile = async (selectedFile) => {
		const data = await selectedFile.arrayBuffer();
		const workbook = read(data, { type: "array" });
		const sheetName = workbook.SheetNames[0];
		const worksheet = workbook.Sheets[sheetName];
		return utils.sheet_to_json(worksheet, { header: 1, defval: "" });
	};

	const validateIncomingTable = React.useCallback(
		(aoa) => {
			if (!aoa || aoa.length < 2) {
				throw new Error("Expected a header row and at least one data row.");
			}

			const header = (aoa[0] || []).map((value) => String(value ?? "").trim());
			const expectedVersions = answerKeyArray.map((key, index) => getVersionLabel(key, index));

			if (header.length !== expectedVersions.length) {
				throw new Error("Header column count does not match expected versions.");
			}

			const duplicateHeaders = header.filter(
				(value, index) => value && header.indexOf(value) !== index,
			);
			if (duplicateHeaders.length > 0) {
				throw new Error(
					`Duplicate version header(s) found: ${[...new Set(duplicateHeaders)].join(", ")}.`,
				);
			}

			const missingVersions = expectedVersions.filter((version) => !header.includes(version));
			const unexpectedVersions = header.filter((version) => !expectedVersions.includes(version));
			if (missingVersions.length > 0 || unexpectedVersions.length > 0) {
				const parts = [];
				if (missingVersions.length > 0) parts.push(`missing ${missingVersions.join(", ")}`);
				if (unexpectedVersions.length > 0) parts.push(`unexpected ${unexpectedVersions.join(", ")}`);
				throw new Error(
					`Version headers must match the loaded key versions exactly: ${parts.join("; ")}.`,
				);
			}

			const body = aoa.slice(1).map((row) => row.map((value) => String(value ?? "").trim()));
			const baseCandidates = header
				.map((versionLabel, columnIndex) => {
					const rowCount = getRowCountForVersion(versionLabel);
					if (!rowCount || body.length < rowCount) return null;
					const isIdentity = body
						.slice(0, rowCount)
						.every(
							(row, rowIndex) => String(row?.[columnIndex] ?? "") === String(rowIndex + 1),
						);
					if (!isIdentity) return null;
					return { versionLabel, columnIndex, rowCount };
				})
				.filter(Boolean);

			if (baseCandidates.length === 0) {
				throw new Error(
					"One version column must contain a 1..N identity mapping so the base version can be detected.",
				);
			}

			const detectedBase = baseCandidates[0];
			const width = header.length;
			const trimmed = body.slice(0, detectedBase.rowCount).map((row) => {
				const copy = row.slice(0, width);
				while (copy.length < width) copy.push("");
				return copy;
			});

			for (let rowIndex = 0; rowIndex < trimmed.length; rowIndex++) {
				for (let columnIndex = 0; columnIndex < width; columnIndex++) {
					const raw = trimmed[rowIndex]?.[columnIndex] ?? "";
					if (raw === "") continue;
					if (!/^\d+$/.test(raw)) {
						throw new Error(
							`Row ${rowIndex + 1}, version '${header[columnIndex]}' must contain only integers or blanks.`,
						);
					}
				}
			}

			return {
				headers: header,
				rows: trimmed,
				baseVersion: detectedBase.versionLabel,
				baseColumnIndex: detectedBase.columnIndex,
				baseItemNos: Array.from({ length: detectedBase.rowCount }, (_, index) => index + 1),
			};
		},
		[answerKeyArray, getRowCountForVersion, getVersionLabel],
	);

	const handleFileChange = async (event) => {
		setError("");
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		const name = (selectedFile.name || "").toLowerCase();
		if (!name.endsWith(".xlsx") && !name.endsWith(".csv")) {
			setError("Please upload a .xlsx or .csv file.");
			return;
		}

		setFile(selectedFile);
		setBusy(true);
		try {
			let aoa;
			if (name.endsWith(".xlsx")) aoa = await parseXLSXFile(selectedFile);
			else aoa = parseCSVText(await selectedFile.text());

			const validated = validateIncomingTable(aoa);
			setVersions(validated.headers);
			setRows(validated.rows);
			setBaseVersion(validated.baseVersion);
			setBaseColumnIndex(validated.baseColumnIndex);
			setBaseItemNos(validated.baseItemNos);
		} catch (err) {
			console.error(err);
			setError(err?.message || "Failed to parse file.");
			setFile(null);
		} finally {
			setBusy(false);
		}
	};

	const handleDownloadTemplate = () => {
		if (!versions.length || !baseItemNos.length) return;
		const aoa = [versions];
		for (let rowIndex = 0; rowIndex < baseItemNos.length; rowIndex++) {
			const row = new Array(versions.length).fill("");
			row[baseColumnIndex] = String(baseItemNos[rowIndex]);
			aoa.push(row);
		}
		const worksheet = utils.aoa_to_sheet(aoa);
		const workbook = utils.book_new();
		utils.book_append_sheet(workbook, worksheet, "Reorder");
		writeFileXLSX(workbook, "reorder-template.xlsx");
	};

	const handleReupload = () => {
		setFile(null);
		setError("");
	};

	const handleNormalize = async (action) => {
		try {
			if (!versions.length || !rows.length) throw new Error("No table data to submit.");
			if (!examProcessingState) throw new Error("Missing exam state.");

			const resolvedBaseVersion = baseVersion || versions[baseColumnIndex] || versions[0] || null;
			if (!resolvedBaseVersion) {
				throw new Error("Unable to determine the base version for reorder normalization.");
			}

			const reorderMap = {};
			for (let columnIndex = 0; columnIndex < versions.length; columnIndex++) {
				if (columnIndex === baseColumnIndex) continue;
				const version = versions[columnIndex];
				reorderMap[version] = rows.map((row) => {
					const raw = (row?.[columnIndex] ?? "").trim();
					if (raw === "") return null;
					const parsed = Number.parseInt(raw, 10);
					return Number.isFinite(parsed) ? parsed : null;
				});
			}

			setBusy(true);
			setError("");
			const payload = {
				header: examProcessingState.header,
				answer_keys: examProcessingState.answer_keys,
				student_responses: examProcessingState.students,
				reorder_map: reorderMap,
				base_version: resolvedBaseVersion,
				strict_reorder_version_match: strictVersionMatch,
			};
			const response = await processExam(token, reqnum, false, payload);
			if (response && Array.isArray(response.analyses_by_version)) {
				onNormalized?.(response.analyses_by_version, action);
			}
			if (!embedded) handleClose();
		} catch (err) {
			console.error(err);
			setError(
				err?.response?.data?.error ||
					err?.message ||
					"Normalization/verification failed. Ensure headers match the key versions and all mapped questions have identical answers.",
			);
		} finally {
			setBusy(false);
		}
	};

	const content = (
		<Stack spacing={2}>
			<Typography variant="body2">
				Download the template file using the button below, paste in your re-order mapping for
				the remaining answer keys, then re-upload your completed template file using the "Choose
				File" button. The uploaded version headers can be in any order, but they must match the
				loaded version codes exactly and one column must contain the identity sequence 1..N to
				establish the base version. Answers must still match exactly across versions.
			</Typography>
			<Typography variant="body2">
				Important: Your re-order mapping will only persist while your current window and browser
				session are active/open to this exam request. If you complete this re-order and download
				your results, but want to come back later to get your results again, you will have to
				upload your re-order file again to get your combined item analysis.
			</Typography>
			<FormControlLabel
				control={
					<Switch
						checked={strictVersionMatch}
						onChange={(event) => setStrictVersionMatch(event.target.checked)}
					/>
				}
				label="Strict version mapping (recommended)"
			/>
			<Typography variant="caption" color="text.secondary">
				When enabled, normalization fails if student versions are not mapped. When disabled,
				unmapped versions are scored as base-order responses.
			</Typography>

			<div className="flex gap-2 items-center">
				<Button
					variant="outlined"
					onClick={handleDownloadTemplate}
					disabled={!versions.length || !baseItemNos.length}
				>
					Download Template
				</Button>
			</div>

			<div className="flex gap-8 items-center">
				<input type="file" accept=".xlsx,.csv" onChange={handleFileChange} disabled={busy} />
				{file && (
					<Button onClick={handleReupload} disabled={busy}>
						Choose Different File
					</Button>
				)}
			</div>

			{busy && <LinearProgress />}

			{error && (
				<Typography color="error" variant="body2">
					{error}
				</Typography>
			)}

			{versions.length > 0 && rows.length > 0 && (
				<TableContainer component={Paper} sx={{ maxHeight: 480 }}>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								{versions.map((version, index) => (
									<TableCell
										key={index}
										sx={{
											whiteSpace: "nowrap",
											fontWeight: index === baseColumnIndex ? 700 : 400,
											backgroundColor:
												index === baseColumnIndex ? "primary.50" : undefined,
										}}
									>
										{version} (Version{version === baseVersion ? " Base" : ""})
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map((row, rowIndex) => (
								<TableRow key={rowIndex} hover>
									{versions.map((_, columnIndex) => (
										<TableCell
											key={columnIndex}
											sx={{
												backgroundColor:
													columnIndex === baseColumnIndex ? "primary.50" : undefined,
											}}
										>
											{row[columnIndex] ?? ""}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{versions.length > 0 && rows.length === 0 && baseItemNos.length > 0 && (
				<TableContainer component={Paper} sx={{ maxHeight: 480 }}>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								{versions.map((version, index) => (
									<TableCell
										key={index}
										sx={{
											whiteSpace: "nowrap",
											fontWeight: index === baseColumnIndex ? 700 : 400,
											backgroundColor:
												index === baseColumnIndex ? "primary.50" : undefined,
										}}
									>
										{version}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{baseItemNos.map((itemNo, rowIndex) => (
								<TableRow key={rowIndex} hover>
									{versions.map((_, columnIndex) => (
										<TableCell
											key={columnIndex}
											sx={{
												backgroundColor:
													columnIndex === baseColumnIndex ? "primary.50" : undefined,
											}}
										>
											{columnIndex === baseColumnIndex ? String(itemNo) : ""}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{baseItemNos.length > 0 && (
				<Typography variant="caption">
					Rows: {baseItemNos.length} (base version: {baseVersion || versions[baseColumnIndex] || "?"})
				</Typography>
			)}
		</Stack>
	);

	if (embedded) {
		return (
			<div className="flex h-full flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4">
				<div className="flex flex-col gap-1">
					<Typography variant="h6">Re-Order Workspace</Typography>
					<Typography variant="body2" color="text.secondary">
						Create and validate a normalized item analysis mapping across versions in this tab.
					</Typography>
				</div>
				{content}
				<div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
					<Button
						variant="outlined"
						onClick={() => handleNormalize("download")}
						disabled={busy || !versions.length || !baseItemNos.length || !rows.length}
					>
						Normalize &amp; Download
					</Button>
					<Button
						variant="contained"
						onClick={() => handleNormalize("continue")}
						disabled={busy || !versions.length || !baseItemNos.length || !rows.length}
					>
						Continue with normalized analysis
					</Button>
				</div>
			</div>
		);
	}

	return (
		<Dialog open={open} onClose={busy ? undefined : handleClose} maxWidth="xl" fullWidth>
			<DialogTitle>
				Re-Order (Normalized Item Analysis across versions)
				<button type="button" className="absolute top-3 right-3" onClick={handleClose}>
					<span className="sr-only">Close</span>
					<svg
						className="h-6 w-6 text-gray-500 hover:text-gray-700"
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						aria-hidden="true"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M6 18L18 6M6 6l12 12"
						/>
					</svg>
				</button>
			</DialogTitle>
			<DialogContent dividers>{content}</DialogContent>
			<DialogActions sx={{ justifyContent: "space-between" }}>
				<Button onClick={handleClose} disabled={busy}>
					Cancel
				</Button>
				<div className="flex gap-2 ">
					<Button
						variant="outlined"
						onClick={() => handleNormalize("download")}
						disabled={busy || !versions.length || !baseItemNos.length || !rows.length}
					>
						Normalize &amp; Download
					</Button>
					<Button
						variant="contained"
						onClick={() => handleNormalize("continue")}
						disabled={busy || !versions.length || !baseItemNos.length || !rows.length}
					>
						Continue with normalized analysis
					</Button>
				</div>
			</DialogActions>
		</Dialog>
	);
}
